import React, { useState } from 'react';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import { brandColors } from '@config/brandTokens';

/** Compact brand-blue CTA — same size for Resend verification and Set Password. */
export const FAMILY_CHILD_ACTION_BUTTON_SX = {
  textTransform: 'none' as const,
  fontWeight: 700,
  fontSize: '0.8125rem',
  lineHeight: 1.25,
  minHeight: 32,
  height: 32,
  py: 0,
  px: 1.25,
  width: 'auto',
  minWidth: 'unset',
  whiteSpace: 'nowrap',
  bgcolor: brandColors.blue,
  color: brandColors.white,
  boxShadow: 'none',
  '&:hover': { bgcolor: '#3a56c9', boxShadow: 'none' },
  '&.Mui-disabled': { color: '#fff', bgcolor: '#9bb0f0' },
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export function childConsentPending(row: { consentVerified?: boolean; consentStatus?: string }) {
  if (row.consentStatus === 'pending') return true;
  if (row.consentStatus === 'verified') return false;
  return row.consentVerified === false;
}

export async function resendChildParentalConsent(playerId: string) {
  const res = await fetch(`/api/children/${encodeURIComponent(playerId)}/resend-consent`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const text = await res.text();
  let json: {
    success?: boolean;
    message?: string;
    data?: { emailedTo?: string; emailSource?: string; skipped?: boolean };
  } = {};
  if (text.trim()) {
    try {
      json = JSON.parse(text);
    } catch {
      json = {};
    }
  }
  if (!res.ok || !json.success) {
    throw new Error(json.message || 'Could not send the verification email');
  }
  return json as {
    success: true;
    message: string;
    data?: { emailedTo?: string; emailSource?: string; skipped?: boolean };
  };
}

export type ResendChildConsentButtonProps = {
  playerId: string;
  pending?: boolean;
  label?: string;
};

const ResendChildConsentButton: React.FC<ResendChildConsentButtonProps> = ({
  playerId,
  pending = true,
  label = 'Resend verification',
}) => {
  const [sending, setSending] = useState(false);
  if (!pending || !playerId) return null;

  const send = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const json = await resendChildParentalConsent(playerId);
      if (json.data?.skipped) {
        toast.info(json.message);
      } else {
        toast.success(json.message);
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not send the verification email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      size="small"
      variant="contained"
      disabled={sending}
      onClick={send}
      sx={FAMILY_CHILD_ACTION_BUTTON_SX}
    >
      {sending ? 'Sending...' : label}
    </Button>
  );
};

export default ResendChildConsentButton;
